import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import InputField from '../../core/Input/TextInput/InputField';
import Button from '../../core/Button/Button';

type VerifyFormProps = {
  onSubmit: (data: any) => Promise<void>;
  onExpire: () => void;
  expiresAt: number;
  isSubmitting: boolean;
  formTitle: string;
  buttonTitle: string;
};

const schema = z.object({
  otp: z.string().min(5, 'کد وارد شده حداقل می بایست ۵ رقمی باشد'),
});

const calculateRemainingTime = (expiresAt: number) => {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
};

const VerifyForm: React.FC<VerifyFormProps> = ({
  onSubmit,
  onExpire,
  expiresAt,
  isSubmitting,
  formTitle,
  buttonTitle,
}) => {
  const [remainingTime, setRemainingTime] = useState(() =>
    calculateRemainingTime(expiresAt)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const updateTimer = () => {
      const newRemainingTime = calculateRemainingTime(expiresAt);

      setRemainingTime(newRemainingTime);

      if (newRemainingTime <= 0) {
        onExpire();
      }
    };

    updateTimer();

    const timer = window.setInterval(updateTimer, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [expiresAt, onExpire]);

  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;

  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <h1 className="text-center text-xl text-[#3c5caa] font-bold">
        {formTitle}
      </h1>
      <InputField
        containerClassName="text-start text-black py-2"
        inputClassName="text-center"
        label="کد تایید را وارد کنید"
        type="text"
        placeholder="*****"
        error={
          typeof errors.otp?.message === 'string'
            ? errors.otp.message
            : undefined
        }
        {...register('otp')}
      />
      <div className="text-center text-sm text-gray-500">
        زمان باقی‌مانده:
        <span
          className={`mr-2 font-bold ${
            remainingTime <= 30 ? 'text-red-500' : 'text-[#3c5caa]'
          }`}
          dir="ltr"
        >
          {formattedTime}
        </span>
      </div>
      <Button disabled={isSubmitting || remainingTime <= 0}>
        {isSubmitting ? 'در حال ارسال...' : buttonTitle}
      </Button>
    </form>
  );
};

export default VerifyForm;
